/**
 * Flagged-node (`valid = false`) list support for the Settings "Graph
 * properties" surface. Invalidation is otherwise write-only — easy to set,
 * invisible to find later — so this module gives flagged nodes a home: a
 * flat cypher lookup plus a row mapper that surfaces the "why" from the
 * node's latest `manual` provenance evidence (written by the
 * record-evidence endpoint / `propose_node_evidence` tool).
 */

import { latestManualEvidence, type Provenance } from '@atbash-labs/ontology-core'

import { asString, mapCypherNodeIdentity } from './cypher-row'

/**
 * Explicit `SKIP/LIMIT` tail so `drainCypherRows` can page through the
 * full set rather than stopping at the proxy's first page.
 */
export const FLAGGED_NODES_CYPHER = `
MATCH (n)
WHERE n.valid = false
RETURN n.id AS id, elementId(n) AS element_id, n.name AS name, labels(n) AS labels,
       n.tag_path AS tag_path, n.folder_name AS folder_name, n.provenance AS provenance
ORDER BY toLower(coalesce(n.name, n.id, ''))
SKIP toInteger($skip) LIMIT toInteger($pageSize)
`.trim()

/** One row on the flagged-nodes list. */
export interface FlaggedNode {
  /** Business id when present, else the Neo4j element id (both resolve on the evidence endpoint). */
  id: string
  name?: string
  labels: string[]
  /** Browse context: `tag_path` when present, else `folder_name`. */
  path?: string
  /** The "why" from the latest `manual` provenance evidence, when one exists. */
  reason?: string
  flagged_by?: string
  flagged_at?: string
}

/** Body for POST /nodes/evidence — records a manual note and (optionally) asserts `valid` / review. */
export interface RecordNodeEvidenceArgs {
  nodeId: string
  note: string
  valid?: boolean
  needsReview?: boolean
}

export interface RecordNodeEvidenceResult {
  node?: Record<string, unknown>
  counters?: Record<string, number>
}

/**
 * Provenance is stored JSON-serialized in Neo4j (nested maps aren't
 * representable), so `/cypher/read` returns it as a string. Tolerate an
 * already-parsed object too; anything unparseable just yields no reason.
 */
export function parseProvenance(value: unknown): Provenance | undefined {
  if (value !== null && typeof value === 'object') return value as Provenance
  if (typeof value === 'string' && value.trim().length > 0) {
    try {
      const parsed: unknown = JSON.parse(value)
      if (parsed !== null && typeof parsed === 'object') return parsed as Provenance
    } catch {
      return undefined
    }
  }
  return undefined
}

/** Map one cypher row to a `FlaggedNode`; null when the row carries no usable id. */
export function mapFlaggedNodeRow(row: Record<string, unknown>): FlaggedNode | null {
  const identity = mapCypherNodeIdentity(row)
  if (identity === null) return null

  const node: FlaggedNode = {
    id: identity.id,
    labels: identity.labels,
    ...(identity.name !== undefined ? { name: identity.name } : {}),
    ...(identity.path !== undefined ? { path: identity.path } : {})
  }

  const provenance = parseProvenance(row.provenance)
  const manual = provenance !== undefined ? latestManualEvidence(provenance) : undefined
  if (manual !== undefined) {
    const reason = asString(manual.source_excerpt)
    if (reason !== undefined) node.reason = reason
    const by = asString(manual.created_by)
    if (by !== undefined) node.flagged_by = by
    const at = asString(manual.created_at)
    if (at !== undefined) node.flagged_at = at
  }
  return node
}
